import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Button from '@ui-elements/Button';
import useHandleLogout from '@functional/useHandleLogout';
import { callThunk, withdrawUser } from '~/api';
import { FONT_SIZES, PALETTE } from '~/assets/styles/base';
import { GMOASK_STATUS } from '~/constants/api';
import { ROUTES } from '~/constants/routes';

const Wrapper = styled.div`
  width: 716px;
  margin: 0 auto;
  background-color: ${PALETTE.white};
  border: 1px solid ${PALETTE.border.frame};
  margin-top: 94px;
  padding: 58px 68px;
`;

const TitleWrapper = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const Title = styled(Typography)`
  color: ${PALETTE.text.primary};
  margin-bottom: 34px;
`;

const Reasons = styled.div`
  margin-top: 43px;
  padding-bottom: 18px;
  border-bottom: 1px solid #d4ddde;
  line-height: 1.4;
`;

const ReasonsList = styled.ul`
  margin-top: 18px;
`;

const ReasonsListItem = styled.li`
  font-size: ${FONT_SIZES.bodyDefault};
  margin-bottom: 16px;
`;
const OtherReasonTitle = styled(ReasonsListItem)`
  margin-bottom: 12px;
`;

const Opinion = styled.div`
  margin-top: 30px;
`;

const NoRequiredGmoResearchInfo = styled.div`
  margin-top: 30px;
`;

const OpinionDetail = styled.div`
  font-size: ${FONT_SIZES.bodyDefault};
  margin-top: 20px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 68px;
`;

const SendButton = styled(Button)`
  display: block;
  min-width: 146px;
  height: 40px;
  margin-left: 21px;
  font-weight: normal;
`;

const RewindButton = styled(Button)`
  display: block;
  min-width: 146px;
  height: 40px;
  font-weight: normal;
`;

/**
 * 自主退会確認画面 表示内容をlocation stateを受け取りそのまま表示する
 * location stateがない場合は自主退会画面にリダイレクト
 */
function WithdrawConfirm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const handleLogout = useHandleLogout({ isAdmin: false });
  const { t } = useTranslation();
  const { state } = location;

  const handleOnClickSend = async () => {
    // チェックされている項目に"✓"を入れる。
    const params = {
      reasons: [
        ...state.reasons.map((reason) => (reason.checked ? `${reason.text}✓` : reason.text)),
        state.otherReason.checked
          ? `${state.otherReason.text}(${state.otherReason.value})✓`
          : `${state.otherReason.text}()`,
        state.noRequiredGmoResearchInfo.checked // パラメータとしてreasonsに含める
          ? `${state.noRequiredGmoResearchInfo.text}✓`
          : `${state.noRequiredGmoResearchInfo.text}`,
      ],
      opinion: state.opinion,
    };

    const res = await dispatch(
      callThunk({
        action: withdrawUser,
        payload: params,
      })
    );
    /* istanbul ignore next OK以外はエラー処理に移る為、帰ってこないが念の為 */
    if (res.payload.status === GMOASK_STATUS.OK) {
      handleLogout(ROUTES.USER.WITHDRAW_COMPLETED);
    }
  };

  // 直アクセス(location.stateが未設定)は退会画面にリダイレクト
  useEffect(() => {
    if (state == null) navigate(ROUTES.USER.WITHDRAW);
  }, []);

  return (
    <Wrapper>
      <TitleWrapper>
        <Title variant="title1" component="p">
          {t('withdrawConfirm.title')}
        </Title>
        <Typography variant="bodyDefault">{t('withdrawConfirm.sub')}</Typography>
      </TitleWrapper>

      <Reasons>
        <Typography variant="title3">{t('withdrawConfirm.reason')}</Typography>
        <ReasonsList data-testid="WithdrawConfirm-ReasonsList">
          {state?.reasons?.map(
            (reason) =>
              reason?.checked && (
                <ReasonsListItem key={reason?.text}>{reason?.text}</ReasonsListItem>
              )
          )}
          {state?.otherReason?.checked && (
            <>
              <OtherReasonTitle>{state?.otherReason?.text}</OtherReasonTitle>
              <ReasonsListItem data-testid="WithdrawConfirm-otherReason">
                {state?.otherReason?.value}
              </ReasonsListItem>
            </>
          )}
        </ReasonsList>
      </Reasons>

      {state?.noRequiredGmoResearchInfo?.checked && (
        <NoRequiredGmoResearchInfo>
          <div data-testid="WithdrawConfirm-noRequiredGmoResearchInfo">
            {state?.noRequiredGmoResearchInfo?.text}
          </div>
        </NoRequiredGmoResearchInfo>
      )}

      {state?.opinion && (
        <Opinion>
          <Typography variant="title3">{t('withdrawConfirm.opinion')}</Typography>
          <OpinionDetail data-testid="WithdrawConfirm-opinion">{state?.opinion}</OpinionDetail>
        </Opinion>
      )}

      <Buttons>
        <RewindButton
          color="secondary"
          colorType="gray"
          size="small"
          onClick={() => {
            navigate(ROUTES.USER.WITHDRAW, {
              state: {
                reasons: state?.reasons,
                otherReason: state?.otherReason,
                noRequiredGmoResearchInfo: state?.noRequiredGmoResearchInfo,
                opinion: state?.opinion,
                referrer: window.location.pathname,
              },
            });
          }}
          data-testid="WithdrawConfirm-backButton"
        >
          {t('withdrawConfirm.back')}
        </RewindButton>
        <SendButton
          type="button"
          color="primary"
          variant="contained"
          onClick={() => {
            handleOnClickSend();
          }}
          data-testid="WithdrawConfirm-SendButton"
        >
          {t('withdrawConfirm.send')}
        </SendButton>
      </Buttons>
    </Wrapper>
  );
}

export default WithdrawConfirm;
